<script setup lang="ts">
import { computed } from 'vue'
import { TButton } from '@theme/atoms'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import { NETWORK_ERROR_COPY, type NetworkErrorState } from '@/api/network-error'

const { online, currentError, retry, clear } = useNetworkStatus()
const offline = computed(() => !online.value)

const displayedError = computed<NetworkErrorState | null>(() => {
  if (currentError.value) {
    return currentError.value
  }
  if (!online.value) {
    return {
      kind: 'offline',
      ...NETWORK_ERROR_COPY.offline,
    }
  }
  return null
})

const shouldShow = computed(() => offline.value || currentError.value !== null)
</script>

<template>
  <section
    v-if="shouldShow"
    class="network-status-banner"
    role="alert"
    aria-live="assertive"
  >
    <div class="network-status-banner__content">
      <p class="network-status-banner__title">{{ displayedError?.title }}</p>
      <p class="network-status-banner__message">{{ displayedError?.message }}</p>
    </div>
    <div class="network-status-banner__actions">
      <TButton
        v-if="displayedError?.retryable"
        variant="outline"
        size="sm"
        @click="retry"
      >
        重試
      </TButton>
      <button type="button" class="network-status-banner__close" @click="clear">
        關閉
      </button>
    </div>
  </section>
</template>

<style scoped>
.network-status-banner {
  position: fixed;
  top: var(--spacing-md);
  left: var(--spacing-md);
  right: var(--spacing-md);
  z-index: var(--z-index-system-banner);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: calc(var(--spacing-sm) + var(--spacing-xs)) 16px;
  border-radius: var(--radius-md);
  border: 1px solid var(--colors-error);
  background-color: var(--colors-background-elevated);
  color: var(--colors-text-primary);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
}

.network-status-banner__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.network-status-banner__title {
  font-size: var(--typography-body-default-size);
  font-weight: 600;
  margin: 0;
}

.network-status-banner__message {
  font-size: var(--typography-body-small-size);
  margin: 0;
  color: var(--colors-text-secondary);
}

.network-status-banner__actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.network-status-banner__close {
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--colors-text-primary);
  cursor: pointer;
}
</style>
